<template>
  <div>
    <e-label>Componentes Curriculares</e-label>

    <e-autocomplete
      id="componentesCurriculares.busca"
      :items="componentes"
      item-value="id"
      :item-text="(c) => c.descricao"
      :item-disabled="(c) => c.status === 'bloqueado' || c.status === 'vinculado_com_registro'"
      v-model="valueLocal"
      multiple
      chips
      :return-object="false"
      label="Selecione os componentes curriculares..."
      :loading="carregando"
      dense
      solo
      hide-details
    >
      <template #selection="{ item }">
        <v-chip
          :key="item.id"
          small
          class="ma-1"
          :close="item.status !== 'vinculado_com_registro'"
          :color="item.status === 'vinculado_com_registro' ? 'grey lighten-3' : 'primary'"
          :dark="item.status !== 'vinculado_com_registro'"
          @click:close="pedirConfirmacaoRemocao(item)"
        >
          <v-icon v-if="item.status === 'vinculado_com_registro'" left x-small>mdi-lock</v-icon>
          {{ item.descricao }}
        </v-chip>
      </template>

      <template #item="{ item }">
        <v-list-item-icon v-if="item.status === 'bloqueado'" class="mr-2">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small color="grey">mdi-lock</v-icon>
            </template>
            <span>{{ mensagemBloqueio(item) }}</span>
          </v-tooltip>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title :class="{ 'grey--text': item.status === 'bloqueado' }">
            {{ item.descricao }}
            <span v-if="item.status === 'vinculado_com_registro'" class="text-caption grey--text">
              (já vinculado)
            </span>
          </v-list-item-title>
        </v-list-item-content>
      </template>
    </e-autocomplete>

    <div class="mt-2 grey--text text-caption">
      {{ value.length }} de {{ componentes.length }} componentes selecionados
    </div>

    <v-alert dense text type="info" class="mt-2 mb-0">
      Ao selecionar mais de um componente curricular, a gestão será criada como
      <strong>Polivalente</strong>.
    </v-alert>

    <v-alert dense text type="success" class="mt-2 mb-0">
      O componente só pode ser removido se não possuir aulas, notas ou planos registrados.
    </v-alert>

    <div class="mt-3">
      <!-- Legenda dos estados dos componentes curriculares -->
      <v-row>
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="text-subtitle-2 py-2">Legenda:</v-card-title>
            <v-card-text>
              <v-row dense>
                <v-col cols="12" sm="6" class="d-flex align-center mb-1">
                  <v-icon color="primary" class="mr-2">mdi mdi-checkbox-marked</v-icon>
                  <!-- <span class="text-caption"> -->
                  Componente já vinculado (não pode desmarcar)
                  <!-- </span> -->
                </v-col>
                <v-col cols="12" sm="6" class="d-flex align-center mb-1">
                  <v-icon class="mr-2">mdi mdi-lock</v-icon>
                  <!-- <span class="text-caption"> -->
                  Já vinculado em outra gestão na turma
                  <!-- </span> -->
                </v-col>
                <v-col cols="12" sm="6" class="d-flex align-center mb-1">
                  <v-icon class="mr-2">mdi mdi-checkbox-blank-outline</v-icon>
                  <!-- <span class="text-caption"> -->
                  Disponível para adicionar
                  <!-- </span> -->
                </v-col>
                <v-col cols="12" sm="6" class="d-flex align-center mb-1">
                  <v-icon color="success" class="mr-2">mdi mdi-minus-circle</v-icon>
                  <!-- <span class="text-caption"> -->
                  Não possui registros (pode desmarcar)
                  <!-- </span> -->
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-dialog
      :value="!!componenteParaRemover"
      max-width="400"
      @input="(aberto) => !aberto && cancelarRemocao()"
    >
      <v-card v-if="componenteParaRemover">
        <v-card-title class="text-h6">Remover componente curricular?</v-card-title>
        <v-card-text>
          Tem certeza que deseja remover <strong>{{ componenteParaRemover.descricao }}</strong
          >? As células da grade de dias e horários vinculadas a esse componente também serão
          removidas.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelarRemocao">Cancelar</v-btn>
          <v-btn color="error" text @click="confirmarRemocao">Remover</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "EPainelComponentesCurriculares",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    componentes: {
      type: Array,
      default: () => [],
    },
    carregando: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      componenteParaRemover: null,
    };
  },
  computed: {
    valueLocal: {
      get() {
        return this.value;
      },
      set(novosIds) {
        this.$emit("input", novosIds);
      },
    },
  },
  methods: {
    pedirConfirmacaoRemocao(item) {
      this.componenteParaRemover = item;
    },
    cancelarRemocao() {
      this.componenteParaRemover = null;
    },
    // O slot #selection é customizado (chip com cadeado pros já vinculados), então perde a
    // remoção automática que o v-autocomplete faz nos chips padrão — precisa tirar o id do
    // value manualmente ao clicar no "x".
    confirmarRemocao() {
      const id = this.componenteParaRemover.id;
      this.valueLocal = this.value.filter((existingId) => existingId !== id);
      this.componenteParaRemover = null;
    },
    mensagemBloqueio(componente) {
      const professor = componente.bloqueado_por && componente.bloqueado_por.professor;
      return professor
        ? `Já existe uma gestão com esse componente curricular nessa turma (${professor}).`
        : "Já existe uma gestão com esse componente curricular nessa turma.";
    },
  },
};
</script>
